// ─── Marketing mode (iframe side) ──────────────────────────────────────────
// This DTrader build is embedded same-origin under /manual-trader/ inside the
// parent app. The parent presents a marketing account's demo as a real USD
// account with its own play-out ledger. Because we're same-origin we read the
// SAME localStorage the parent writes — `marketing_active` (the on/off flag)
// and `marketing_ledger` (the dual USD/Demo balances) — so the iframe shows the
// identical USD/balance and feeds the same ledger when a trade settles here.
// The browser `storage` event auto-syncs the two frames; for same-frame updates
// we also emit a `marketing-ledger-changed` CustomEvent.

// Opaque keys — must match the parent app (src/utils/marketing-ledger.ts). Nothing
// here reads as a marketing/demo/ledger tell in DevTools.
const LEDGER_KEY = '_dz1';
const ACTIVE_KEY = '_dz0';
export const MARKETING_LEDGER_EVENT = '_dz1c';

type MarketingLedger = {
    loginid: string;
    usd: number;
    demo: number;
    active: 'USD' | 'DEMO';
};

// Persisted JSON is opaque: {l: loginid, a: usd, b: demo, s: 'a'|'b' (active USD|DEMO)}.
// Map to/from the readable MarketingLedger here (in lockstep with the parent app).
const serializeLedger = (l: MarketingLedger): string =>
    JSON.stringify({ l: l.loginid, a: l.usd, b: l.demo, s: l.active === 'DEMO' ? 'b' : 'a' });

export const isMarketingActive = (): boolean => {
    try {
        return localStorage.getItem(ACTIVE_KEY) === '1';
    } catch {
        return false;
    }
};

export const readMarketingLedger = (): MarketingLedger | null => {
    try {
        const raw = localStorage.getItem(LEDGER_KEY);
        if (!raw) return null;
        const p = JSON.parse(raw) || {};
        return {
            loginid: p.l || '',
            usd: Number(p.a) || 0,
            demo: Number(p.b) || 0,
            active: p.s === 'b' ? 'DEMO' : 'USD',
        };
    } catch {
        return null;
    }
};

// Write a settled trade's net profit to the active ledger (shared with parent).
// Clamped at 0 (a busted ledger sits at 0 until the parent reseeds on reload).
export const writeMarketingProfit = (profit: number): void => {
    const l = readMarketingLedger();
    if (!l) return;
    const p = Number(profit);
    if (!Number.isFinite(p)) return;
    const next: MarketingLedger =
        l.active === 'USD' ? { ...l, usd: Math.max(0, l.usd + p) } : { ...l, demo: Math.max(0, l.demo + p) };
    try {
        localStorage.setItem(LEDGER_KEY, serializeLedger(next));
    } catch {
        /* storage unavailable */
    }
    // `storage` fires only in OTHER frames — nudge this frame's listeners too.
    try {
        window.dispatchEvent(new CustomEvent(MARKETING_LEDGER_EVENT));
    } catch {
        /* ignore */
    }
};

// Cross-frame play-out dedup — shared with the parent app via the same
// same-origin localStorage. Both frames see the same demo-account contracts, so
// claim each contract+step exactly once across BOTH frames to avoid doubling the
// ledger move. Returns true if this caller may apply the step, false if already done.
const APPLIED_STEPS_KEY = '_dz2';
const MAX_APPLIED_STEPS = 600;
export const claimMarketingStep = (contractId: string, step: 'stake' | 'settle'): boolean => {
    if (!contractId) return true;
    const token = `${step}:${contractId}`;
    try {
        const raw = localStorage.getItem(APPLIED_STEPS_KEY);
        const list: string[] = raw ? JSON.parse(raw) : [];
        if (list.includes(token)) return false;
        list.push(token);
        if (list.length > MAX_APPLIED_STEPS) list.splice(0, list.length - MAX_APPLIED_STEPS);
        localStorage.setItem(APPLIED_STEPS_KEY, JSON.stringify(list));
        return true;
    } catch {
        return true;
    }
};

// Deduct the stake the moment a trade is placed (balance dips immediately, like
// a real account). The payout is returned later via writeMarketingProfit on a win.
export const debitMarketingStake = (stake: number): void => {
    const l = readMarketingLedger();
    if (!l) return;
    const s = Number(stake);
    if (!Number.isFinite(s) || s <= 0) return;
    const next: MarketingLedger =
        l.active === 'USD' ? { ...l, usd: Math.max(0, l.usd - s) } : { ...l, demo: Math.max(0, l.demo - s) };
    try {
        localStorage.setItem(LEDGER_KEY, serializeLedger(next));
    } catch {
        /* storage unavailable */
    }
    try {
        window.dispatchEvent(new CustomEvent(MARKETING_LEDGER_EVENT));
    } catch {
        /* ignore */
    }
};
